/**
 * Position and tree code codec — dev brief §4.
 *
 * A *position* in the ground and a *tree* growing in it are two different
 * things. The position code is stamped on a physical tag and outlives every
 * tree that ever occupies it; the tree code is the position code plus a
 * generation number. This module is the single source of truth for both
 * formats, and it is deliberately the only place that knows their shape.
 *
 *   Position   SNK-A01-0042       {SITE}-{BLOCK}{ROW}-{POS}
 *   Tree       SNK-A01-0042.1     {position}.{generation}
 *   Legacy     0042               the number painted on the tree
 *   Checked    SNK-A01-0042-K     optional ISO 7064 MOD 37,36 check character
 *
 * At Somsanouk the POS segment IS the painted number, so the tail of the code
 * is what the farmer already knows. That is a deliberate departure from the
 * brief's §4.3 assumption that the two "will generally not correspond" — the
 * client numbers one tree per position, 0001 upward, and matching it removes a
 * translation step in the field.
 *
 * Field staff say `SNK-A03-042` as "A-three, forty-two" and can walk to it
 * without a map — which is the whole reason the code is structured rather than
 * a flat number.
 */

// ---------------------------------------------------------------------------
// Format
// ---------------------------------------------------------------------------

/** 3 chars, uppercase A–Z / 0–9. Immutable once positions exist. */
const SITE_RE = /^[A-Z0-9]{3}$/;

/** 1–2 uppercase letters: A–Z, then AA–ZZ. 702 blocks per site. */
const BLOCK_RE = /^[A-Z]{1,2}$/;

export const ROW_MIN = 1;
export const ROW_MAX = 99;
export const POS_MIN = 1;
/**
 * Stored ceiling. The printed code carries FOUR digits so it matches the
 * numbers already painted on the trees (0001–0625), but the stored value is
 * still capped at 999 by the CHECK constraint on `position.pos_no`.
 *
 * That ceiling is real and worth knowing: it is per row, and Somsanouk's 625
 * trees currently sit in one row. Going past 999 in a single row needs a
 * migration — which is the same moment the plantation should be split into
 * proper blocks and rows anyway.
 */
export const POS_MAX = 999;

/**
 * Full position code, with an optional trailing check character.
 * Groups: site, block, row, pos, check.
 */
const POSITION_RE = /^([A-Z0-9]{3})-([A-Z]{1,2})(\d{2})-(\d{3,4})(?:-([0-9A-Z]))?$/;

/** Tree code: a position code with `.{generation}` appended. */
const TREE_RE = /^(.+?)\.(\d{1,4})$/;

/** The original painted numbering: 1–6 digits, stored zero-padded to 4. */
const LEGACY_RE = /^\d{1,6}$/;

export interface PositionCodeParts {
  site: string;
  block: string;
  row: number;
  pos: number;
  /** Present only when the code carried a check character. */
  check?: string;
}

export interface TreeCodeParts extends PositionCodeParts {
  generation: number;
}

export class PositionCodeError extends Error {
  constructor(
    message: string,
    readonly input: string,
    readonly reason:
      | 'malformed'
      | 'bad_site'
      | 'bad_block'
      | 'row_out_of_range'
      | 'pos_out_of_range'
      | 'bad_check_char'
      | 'bad_generation',
  ) {
    super(message);
    this.name = 'PositionCodeError';
  }
}

// ---------------------------------------------------------------------------
// Check character — ISO 7064 MOD 37,36
// ---------------------------------------------------------------------------

const CHECK_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const MOD = 36;

/**
 * ISO 7064 MOD 37,36 over the code's alphanumeric characters (separators
 * stripped). Chosen over a plain digit-sum because it detects transpositions —
 * `A03-042` mis-keyed as `A03-024` is exactly the error a tired manager makes,
 * and a naive mod-36 sum would wave it through.
 *
 * Disabled in v1 behind `tenant.position_check_char`; the codec supports it
 * from day one so enabling it later is a setting, not a migration.
 */
export function computeCheckChar(codeWithoutCheck: string): string {
  const chars = codeWithoutCheck.toUpperCase().replace(/[^0-9A-Z]/g, '');
  let p = MOD;
  for (const ch of chars) {
    const value = CHECK_ALPHABET.indexOf(ch);
    if (value < 0) {
      throw new PositionCodeError(
        `Character ${ch} is not valid in a position code`,
        codeWithoutCheck,
        'malformed',
      );
    }
    let s = ((p % (MOD + 1)) + value) % MOD;
    if (s === 0) s = MOD;
    p = 2 * s;
  }
  return CHECK_ALPHABET[(MOD + 1 - (p % (MOD + 1))) % MOD]!;
}

/** True if `code` ends in a check character that matches its body. */
export function verifyCheckChar(code: string): boolean {
  const m = POSITION_RE.exec(normalizeCode(code));
  if (!m || !m[5]) return false;
  const body = `${m[1]}-${m[2]}${m[3]}-${m[4]}`;
  return computeCheckChar(body) === m[5];
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Fold the sloppy things a human types into canonical form before parsing:
 * lowercase, stray whitespace, en/em dashes pasted from a document, and
 * underscores or spaces used in place of hyphens.
 *
 * Deliberately does NOT invent missing separators — `SNKA03042` is rejected
 * rather than guessed at, because a wrong guess silently targets the wrong
 * tree and nobody finds out until the work order comes back.
 */
export function normalizeCode(input: string): string {
  return input
    .trim()
    .toUpperCase()
    .replace(/[‐-―_\s]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

// ---------------------------------------------------------------------------
// Position codes
// ---------------------------------------------------------------------------

/**
 * Parse a position code. Throws `PositionCodeError` with a specific `reason`
 * so the API can return a useful 422 rather than "invalid code".
 *
 * @param opts.requireCheckChar when true, a code with no check character, or
 *   one whose check character does not verify, is rejected.
 */
export function parsePositionCode(
  input: string,
  opts: { requireCheckChar?: boolean } = {},
): PositionCodeParts {
  const code = normalizeCode(input);
  const m = POSITION_RE.exec(code);
  if (!m) {
    throw new PositionCodeError(
      `"${input}" is not a position code — expected SITE-BLOCKROW-POS, e.g. SNK-A03-042`,
      input,
      'malformed',
    );
  }

  const [, site, block, rowRaw, posRaw, check] = m as unknown as [
    string,
    string,
    string,
    string,
    string,
    string | undefined,
  ];
  const row = Number(rowRaw);
  const pos = Number(posRaw);

  if (!SITE_RE.test(site)) {
    throw new PositionCodeError(`Site "${site}" must be 3 chars of A–Z or 0–9`, input, 'bad_site');
  }
  if (!BLOCK_RE.test(block)) {
    throw new PositionCodeError(`Block "${block}" must be A–Z or AA–ZZ`, input, 'bad_block');
  }
  if (row < ROW_MIN || row > ROW_MAX) {
    throw new PositionCodeError(
      `Row ${rowRaw} is outside ${ROW_MIN}–${ROW_MAX}`,
      input,
      'row_out_of_range',
    );
  }
  if (pos < POS_MIN || pos > POS_MAX) {
    throw new PositionCodeError(
      `Position ${posRaw} is outside ${POS_MIN}–${POS_MAX}`,
      input,
      'pos_out_of_range',
    );
  }

  if (check !== undefined) {
    const expected = computeCheckChar(`${site}-${block}${rowRaw}-${posRaw}`);
    if (check !== expected) {
      throw new PositionCodeError(
        `Check character "${check}" does not match — this code was probably mis-keyed`,
        input,
        'bad_check_char',
      );
    }
  } else if (opts.requireCheckChar) {
    throw new PositionCodeError(
      `Position code "${code}" is missing its check character`,
      input,
      'bad_check_char',
    );
  }

  return check === undefined ? { site, block, row, pos } : { site, block, row, pos, check };
}

/** Format parts into a canonical position code. */
export function formatPositionCode(
  parts: Omit<PositionCodeParts, 'check'>,
  opts: { withCheckChar?: boolean } = {},
): string {
  const site = parts.site.toUpperCase();
  const block = parts.block.toUpperCase();

  if (!SITE_RE.test(site)) {
    throw new PositionCodeError(`Site "${site}" must be 3 chars of A–Z or 0–9`, site, 'bad_site');
  }
  if (!BLOCK_RE.test(block)) {
    throw new PositionCodeError(`Block "${block}" must be A–Z or AA–ZZ`, block, 'bad_block');
  }
  if (!Number.isInteger(parts.row) || parts.row < ROW_MIN || parts.row > ROW_MAX) {
    throw new PositionCodeError(
      `Row ${parts.row} is outside ${ROW_MIN}–${ROW_MAX}`,
      String(parts.row),
      'row_out_of_range',
    );
  }
  if (!Number.isInteger(parts.pos) || parts.pos < POS_MIN || parts.pos > POS_MAX) {
    throw new PositionCodeError(
      `Position ${parts.pos} is outside ${POS_MIN}–${POS_MAX}`,
      String(parts.pos),
      'pos_out_of_range',
    );
  }

  // Four digits, deliberately: the client's trees are painted 0001–0625, and
  // the farmer reads the tail of the code to find the tree. A code ending in
  // `042` when the paint says `0042` is a code they have to translate, and a
  // translation step in a field is a mistake waiting to happen.
  const body = `${site}-${block}${String(parts.row).padStart(2, '0')}-${String(parts.pos).padStart(4, '0')}`;
  return opts.withCheckChar ? `${body}-${computeCheckChar(body)}` : body;
}

/** Non-throwing variant, for search boxes and bulk paste. */
export function tryParsePositionCode(input: string): PositionCodeParts | null {
  try {
    return parsePositionCode(input);
  } catch {
    return null;
  }
}

export function isPositionCode(input: string): boolean {
  return tryParsePositionCode(input) !== null;
}

// ---------------------------------------------------------------------------
// Tree codes
// ---------------------------------------------------------------------------

/** Build a tree code: `SNK-A03-042` + generation 2 → `SNK-A03-042.2`. */
export function formatTreeCode(positionCode: string, generation: number): string {
  if (!Number.isInteger(generation) || generation < 1) {
    throw new PositionCodeError(
      `Generation must be an integer >= 1, got ${generation}`,
      String(generation),
      'bad_generation',
    );
  }
  return `${normalizeCode(positionCode)}.${generation}`;
}

export function parseTreeCode(input: string): TreeCodeParts {
  const code = normalizeCode(input);
  const m = TREE_RE.exec(code);
  if (!m) {
    throw new PositionCodeError(
      `"${input}" is not a tree code — expected SNK-A03-042.1`,
      input,
      'malformed',
    );
  }
  const generation = Number(m[2]);
  if (generation < 1) {
    throw new PositionCodeError(`Generation must be >= 1, got ${m[2]}`, input, 'bad_generation');
  }
  return { ...parsePositionCode(m[1]!), generation };
}

// ---------------------------------------------------------------------------
// Legacy codes
// ---------------------------------------------------------------------------

/**
 * The painted 0001–0625 numbers are kept as a lookup alias and nothing more.
 * They were assigned before blocks and rows existed, so they do NOT correspond
 * to `pos_no` and must never be used to derive one (dev brief §4.3).
 */
export function isLegacyCode(input: string): boolean {
  return LEGACY_RE.test(input.trim());
}

/** Canonical storage form: zero-padded to at least 4 digits. `42` → `0042`. */
export function normalizeLegacyCode(input: string): string {
  const t = input.trim();
  if (!isLegacyCode(t)) {
    throw new PositionCodeError(`"${input}" is not a legacy code`, input, 'malformed');
  }
  return t.replace(/^0+(?=\d)/, '').padStart(4, '0');
}

// ---------------------------------------------------------------------------
// Universal resolver — what the scan endpoint and the search box both use
// ---------------------------------------------------------------------------

export type ResolvedCode =
  | { kind: 'position'; code: string; parts: PositionCodeParts }
  | { kind: 'tree'; code: string; positionCode: string; parts: TreeCodeParts }
  | { kind: 'legacy'; code: string }
  | { kind: 'partial'; query: string }
  | { kind: 'unknown'; query: string };

/**
 * Classify arbitrary user input: a scanned QR, a pasted list entry, or whatever
 * the manager typed into search. `/p/0042` must work exactly like
 * `/p/SNK-A03-042` (dev brief §4.3), and this is where that promise is kept.
 *
 * `partial` covers the prefix searches the UI brief §7.3 requires — `A03`,
 * `SNK-A03`, `SNK` — which the repository turns into a LIKE query rather than
 * an exact lookup.
 */
export function resolveCode(input: string): ResolvedCode {
  const raw = input.trim();
  if (raw === '') return { kind: 'unknown', query: raw };

  // A scanned QR is a URL: https://farm.somsanoukcoconut.com/p/SNK-A03-042
  const fromUrl = extractCodeFromUrl(raw);
  const candidate = fromUrl ?? raw;

  if (isLegacyCode(candidate)) {
    return { kind: 'legacy', code: normalizeLegacyCode(candidate) };
  }

  const normalized = normalizeCode(candidate);

  if (TREE_RE.test(normalized)) {
    try {
      const parts = parseTreeCode(normalized);
      return {
        kind: 'tree',
        code: normalized,
        positionCode: formatPositionCode(parts),
        parts,
      };
    } catch {
      /* fall through to partial */
    }
  }

  const parts = tryParsePositionCode(normalized);
  if (parts) {
    return { kind: 'position', code: formatPositionCode(parts), parts };
  }

  // Anything that looks like the start of a code is a prefix search.
  if (/^[A-Z0-9]{1,3}(-[A-Z]{1,2}\d{0,2}(-\d{0,4})?)?$/.test(normalized)) {
    return { kind: 'partial', query: normalized };
  }

  return { kind: 'unknown', query: raw };
}

/** Pull the code out of a scanned tag URL, if that is what we were handed. */
function extractCodeFromUrl(input: string): string | null {
  if (!/^https?:\/\//i.test(input)) return null;
  try {
    const { pathname } = new URL(input);
    const m = /\/p\/([^/?#]+)/.exec(pathname);
    return m ? decodeURIComponent(m[1]!) : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Block code arithmetic
// ---------------------------------------------------------------------------

/**
 * Block codes run A…Z then AA…ZZ — 702 per site. This is bijective base-26
 * (spreadsheet column ordering), not plain base-26: there is no zero digit, so
 * `Z` is followed by `AA` rather than `BA`.
 */
export function blockCodeToIndex(code: string): number {
  const c = code.trim().toUpperCase();
  if (!BLOCK_RE.test(c)) {
    throw new PositionCodeError(`Block "${code}" must be A–Z or AA–ZZ`, code, 'bad_block');
  }
  let n = 0;
  for (const ch of c) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** Inverse of `blockCodeToIndex`. 1 → `A`, 26 → `Z`, 27 → `AA`, 702 → `ZZ`. */
export function indexToBlockCode(index: number): string {
  if (!Number.isInteger(index) || index < 1 || index > 702) {
    throw new PositionCodeError(
      `Block index ${index} is outside 1–702 (A–ZZ)`,
      String(index),
      'bad_block',
    );
  }
  let n = index;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Positions addressable by this scheme, per site: 702 × 99 × 999. */
export const POSITIONS_PER_SITE = 702 * ROW_MAX * POS_MAX;
